import React from "react";
import Link from "next/link";
import { SectionTitle, sharedStyles, resolveImage } from "./_shared";
import { ASSET_URL } from "../../../config";
import { applyLang } from "../../../i18n";
import renderIcon from "../../../components/renderIcon";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { getBethHabadLabel, walkMinutesFrom } from "../utils";
import StarRating from "../components/StarRating";

const hotelsListStyles = `
.tr-hlist {
  max-width: 620px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.tr-hlist__card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  overflow: hidden;
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 0;
  box-shadow: 0 1px 2px rgba(0,0,0,.04);
  transition: border-color .15s ease, box-shadow .15s ease, transform .15s ease;
  text-decoration: none;
  color: inherit;
}
.tr-hlist__card--noimg { grid-template-columns: 1fr; }
.tr-hlist__card:hover {
  border-color: #d1d5db;
  box-shadow: 0 10px 28px rgba(0,0,0,.08);
  transform: translateY(-2px);
}
.tr-hlist__thumb {
  position: relative;
  background: #f3f4f6;
  overflow: hidden;
}
.tr-hlist__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform .4s ease;
}
.tr-hlist__card:hover .tr-hlist__thumb img { transform: scale(1.06); }
.tr-hlist__body {
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.tr-hlist__badge {
  align-self: flex-start;
  background: color-mix(in srgb, var(--primary, #1b579e) 14%, white);
  color: var(--primary, #1b579e);
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 6px;
}
.tr-hlist__name {
  font-size: 17px;
  font-weight: 800;
  color: #1a1a2e;
  line-height: 1.25;
  margin: 0;
}
.tr-hlist__stars {
  display: inline-flex;
  gap: 2px;
  color: #f59e0b;
  font-size: 14px;
  letter-spacing: 1px;
}
.tr-hlist__distance {
  margin-top: 4px;
  font-size: 13px;
  color: #374151;
  line-height: 1.4;
}
.tr-hlist__distance strong {
  display: block;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 2px;
}
@media (max-width: 520px) {
  .tr-hlist__card { grid-template-columns: 104px 1fr; }
  .tr-hlist__body { padding: 12px 14px; }
}
`;

// HotelsList : liste verticale compacte de 3 hôtels avec étoiles et distance.
// Variante light complémentaire à PlacesGrid (qui est image-first). Autonome.
export default function HotelsList({ context }) {
    const { org, travel, lang } = context;
    const bethHabadLabel = getBethHabadLabel(travel, lang);

    const places = (travel?.hotelPlaces || [])
        .slice()
        .sort((a, b) => (a.position || 0) - (b.position || 0))
        .slice(0, 3);

    if (places.length === 0) return null;

    const categories = travel?.hotel?.categories || [];
    const categoryById = React.useMemo(() => {
        const map = new Map();
        for (const c of categories) map.set(c.id, c);
        return map;
    }, [categories]);

    const cityName = org.ville || org.name;
    const resolvedTitle = t`Endroits recommandés pour séjourner à ${cityName}`;
    const resolvedHighlight = t`Endroits recommandés`;
    const resolvedUrl = "/travel/hotels";
    const resolvedLabel = t`Tous les logements à ${cityName}`;

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: sharedStyles + hotelsListStyles }} />
            <section className="tr-ui-section">
                <SectionTitle title={resolvedTitle} highlight={resolvedHighlight} />
                <div className="tr-hlist">
                    {places.map((place) => {
                        const localized = applyLang(place, lang);
                        const category = place.categoryId ? categoryById.get(place.categoryId) : null;
                        const localizedCat = category ? applyLang(category, lang) : null;

                        const stars = Math.round(place.stars || 0);
                        const walkMinutes = walkMinutesFrom(org, place);

                        const thumb = place.thumbnail
                            || (Array.isArray(place.images) && place.images[0])
                            || place.image
                            || null;
                        const imgSrc = thumb ? resolveImage(thumb, ASSET_URL) : null;

                        const href = org.BASE_WEB_URL + "/travel/hotels/" + place.id;
                        const cardCls = `tr-hlist__card${imgSrc ? "" : " tr-hlist__card--noimg"}`;

                        return (
                            <Link key={place.id} href={href} legacyBehavior>
                                <a className={cardCls}>
                                    {imgSrc && (
                                        <div className="tr-hlist__thumb">
                                            <img src={imgSrc} alt={localized.name} loading="lazy" />
                                        </div>
                                    )}
                                    <div className="tr-hlist__body">
                                        {localizedCat?.label && (
                                            <span className="tr-hlist__badge">{localizedCat.label}</span>
                                        )}
                                        <h3 className="tr-hlist__name">{localized.name}</h3>
                                        {stars > 0 && (
                                            <span className="tr-hlist__stars" aria-label={`${stars} étoiles`}>
                                                {"★".repeat(stars)}
                                            </span>
                                        )}
                                        <StarRating value={place.note} size={14} />
                                        {walkMinutes != null && (
                                            <div className="tr-hlist__distance">
                                                <strong><Trans>Distance du {bethHabadLabel} :</Trans></strong>
                                                <Trans>{walkMinutes} minutes à pied</Trans>
                                            </div>
                                        )}
                                    </div>
                                </a>
                            </Link>
                        );
                    })}
                </div>

                <Link href={org.BASE_WEB_URL + resolvedUrl} legacyBehavior>
                    <a className="tr-ui-seemore">
                        <span>{resolvedLabel}</span>
                        <span className="tr-ui-seemore__arrow">
                            {renderIcon({ name: "IconChevronRight", source: "tabler" }, 16)}
                        </span>
                    </a>
                </Link>
            </section>
        </>
    );
}
